import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Chip,
  Divider,
  Paper
} from '@material-ui/core';
import {
  Add,
  Delete,
  Edit,
  Visibility,
  VisibilityOff,
  Lock,
  Phone,
  Schedule,
  VpnKey,
  Warning
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  sectionContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#fee',
    borderRadius: theme.spacing(1),
    border: '2px solid #ff0000'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  addButton: {
    backgroundColor: '#ff0000',
    color: 'white',
    '&:hover': {
      backgroundColor: '#cc0000'
    }
  },
  restrictedAreaItem: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: 'white',
    border: '2px solid #ff0000',
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
    overflow: 'visible',
    '&:hover': {
      backgroundColor: 'rgba(255, 0, 0, 0.05)',
      transform: 'translateX(4px)'
    }
  },
  selectedArea: {
    backgroundColor: 'rgba(255, 0, 0, 0.1)',
    borderColor: '#cc0000',
    borderWidth: '3px'
  },
  areaHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5)
  },
  areaName: {
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  areaDetails: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: '1px solid #eee'
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    fontSize: '0.85rem'
  },
  restrictionChip: {
    marginLeft: theme.spacing(1),
    height: 20,
    fontSize: '0.7rem'
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexShrink: 0,
    marginLeft: 'auto'
  },
  warningText: {
    color: theme.palette.error.main,
    fontWeight: 'bold'
  },
  noAccessChip: {
    backgroundColor: '#ff0000',
    color: 'white'
  },
  conditionalChip: {
    backgroundColor: '#ff9800',
    color: 'white'
  },
  restrictedChip: {
    backgroundColor: '#f44336',
    color: 'white'
  }
}));

const RestrictedAreasSection = ({
  restrictedAreas = [],
  onAddArea,
  onEditArea,
  onDeleteArea,
  onToggleVisibility,
  selectedAreaId,
  onSelectArea
}) => {
  const classes = useStyles();

  const getRestrictionChipClass = (type) => {
    switch (type) {
      case 'no-access':
        return classes.noAccessChip;
      case 'time-based':
      case 'open-hours':
      case 'guard-attendant':
        return classes.conditionalChip;
      case 'key-required':
      case 'code-required':
        return classes.restrictedChip;
      case 'appointment':
      default:
        return classes.restrictedChip;
    }
  };

  const getRestrictionLabel = (type) => {
    switch (type) {
      case 'no-access':
        return 'NO ACCESS';
      case 'time-based':
        return 'TIME-BASED';
      case 'open-hours':
        return 'OPEN HOURS';
      case 'guard-attendant':
        return 'SEE GUARD';
      case 'code-required':
        return 'CODE REQUIRED';
      case 'key-required':
        return 'KEY REQUIRED';
      case 'appointment':
        return 'APPOINTMENT REQUIRED';
      default:
        return 'RESTRICTED';
    }
  };

  const formatAccessHours = (hours) => {
    if (!hours?.start || !hours?.end) return 'Hours not set';
    return `${hours.start} - ${hours.end}`;
  };

  const getActiveDays = (days) => {
    if (!days) return 'All days';
    const activeDays = Object.entries(days)
      .filter(([_, active]) => active)
      .map(([day, _]) => day.slice(0, 3).toUpperCase());

    if (activeDays.length === 7) return 'All days';
    if (activeDays.length === 0) return 'No days';
    return activeDays.join(', ');
  };

  const hasAccessSchedule = (restrictionDetails = {}) => {
    const hasHours = Boolean(
      restrictionDetails.accessHours?.start ||
      restrictionDetails.accessHours?.end
    );
    const hasLimitedDays = restrictionDetails.accessDays
      ? Object.values(restrictionDetails.accessDays).some(active => !active)
      : false;

    return hasHours ||
      hasLimitedDays ||
      ['time-based', 'open-hours'].includes(restrictionDetails.restrictionType);
  };

  const getContactRows = (restrictionDetails = {}) => {
    if (Array.isArray(restrictionDetails.contacts) && restrictionDetails.contacts.length) {
      return restrictionDetails.contacts
        .map(contact => ({
          name: contact.contactName || contact.name,
          phone: contact.contactPhone || contact.phone,
          email: contact.contactEmail || contact.email
        }))
        .filter(contact => contact.name || contact.phone || contact.email);
    }

    if (!restrictionDetails.contactName) return [];

    return [{
      name: restrictionDetails.contactName,
      phone: restrictionDetails.contactPhone
    }];
  };

  return (
    <Box className={classes.sectionContainer}>
      <Box className={classes.sectionHeader}>
        <Typography variant="h6">
          <Lock style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Restricted Areas
        </Typography>
        <Button
          className={classes.addButton}
          startIcon={<Add />}
          onClick={onAddArea}
          variant="contained"
          size="small"
        >
          Draw Restricted Area
        </Button>
      </Box>

      <Divider style={{ marginBottom: 16 }} />

      {restrictedAreas.length === 0 ? (
        <Typography variant="body2" color="textSecondary" align="center">
          No restricted areas defined for this property
        </Typography>
      ) : (
        <Box>
          {restrictedAreas.map((area, index) => (
            <Paper
              key={area._id || `area-${index}`}
              className={`${classes.restrictedAreaItem} ${
                selectedAreaId === (area._id || index) ? classes.selectedArea : ''
              }`}
              onClick={() => onSelectArea(area._id || index)}
              elevation={1}
            >
              <Box className={classes.areaHeader}>
                <Box className={classes.areaName}>
                  <Lock fontSize="small" color="error" />
                  <Typography variant="subtitle2">
                    {area.name || `Restricted Area ${index + 1}`}
                  </Typography>
                  <Chip
                    label={getRestrictionLabel(area.restrictionDetails?.restrictionType)}
                    size="small"
                    className={`${classes.restrictionChip} ${
                      getRestrictionChipClass(area.restrictionDetails?.restrictionType)
                    }`}
                  />
                </Box>
                <Box className={classes.actionButtons}>
                  <Tooltip title={area.isHidden ? 'Show' : 'Hide'}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleVisibility(area._id || index);
                      }}
                    >
                      {area.isHidden ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditArea(area);
                      }}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteArea(area._id || index);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box className={classes.areaDetails}>
                {getContactRows(area.restrictionDetails).map((contact, contactIndex) => (
                  <Box
                    key={`${contact.name || 'contact'}-${contactIndex}`}
                    className={classes.detailRow}
                  >
                    <Phone fontSize="small" />
                    <Typography variant="caption">
                      {[contact.name, contact.phone, contact.email].filter(Boolean).join(' - ')}
                    </Typography>
                  </Box>
                ))}

                {(area.restrictionDetails?.keyRequired || area.restrictionDetails?.restrictionType === 'key-required') && (
                  <Box className={classes.detailRow}>
                    <VpnKey fontSize="small" color="error" />
                    <Typography variant="caption" className={classes.warningText}>
                      Key Required
                    </Typography>
                  </Box>
                )}

                {area.restrictionDetails?.restrictionType === 'code-required' && (
                  <Box className={classes.detailRow}>
                    <VpnKey fontSize="small" color="error" />
                    <Typography variant="caption" className={classes.warningText}>
                      Code Required
                      {area.restrictionDetails?.accessCode && ` - ${area.restrictionDetails.accessCode}`}
                    </Typography>
                  </Box>
                )}

                {area.restrictionDetails?.restrictionType === 'guard-attendant' && (
                  <Box className={classes.detailRow}>
                    <Warning fontSize="small" color="error" />
                    <Typography variant="caption" className={classes.warningText}>
                      See Guard/Attendant
                    </Typography>
                  </Box>
                )}

                {hasAccessSchedule(area.restrictionDetails) && (
                  <Box className={classes.detailRow}>
                    <Schedule fontSize="small" />
                    <Typography variant="caption">
                      {formatAccessHours(area.restrictionDetails?.accessHours)}
                      {' • '}
                      {getActiveDays(area.restrictionDetails?.accessDays)}
                    </Typography>
                  </Box>
                )}

                {area.restrictionDetails?.accessNotes && (
                  <Box style={{ marginTop: 8 }}>
                    <Typography variant="caption" style={{ fontStyle: 'italic' }}>
                      Note: {area.restrictionDetails.accessNotes}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default RestrictedAreasSection;
